import * as React from 'react';

export type DelayedStateResult<TState> =
  | {
      readonly status: 'pending';
    }
  | {
      readonly status: 'fallback';
      readonly delayed: boolean;
      readonly state?: TState;
    }
  | {
      readonly status: 'completed';
      readonly state: TState;
    };

export interface DelayedStateConfig<TState> {
  readonly initialState?: TState;
  readonly threshold?: number;
  readonly delay?: number;
}

export function useDelayedState<TState>({
  initialState,
  threshold = 150,
  delay = 1200,
}: DelayedStateConfig<TState> = {}): [
  DelayedStateResult<TState>,
  (state: TState | undefined) => void,
] {
  const [result, setResult] = React.useState<DelayedStateResult<TState>>(
    initialState === undefined
      ? {status: `pending`}
      : {status: `completed`, state: initialState},
  );

  const pendingTimeoutHandle = React.useRef<number>();
  const delayedTimeoutHandle = React.useRef<number>();

  React.useEffect(
    () => () => {
      window.clearTimeout(pendingTimeoutHandle.current);
      window.clearTimeout(delayedTimeoutHandle.current);
    },
    [],
  );

  React.useEffect(() => {
    const handleDelayedTimeout: React.SetStateAction<
      DelayedStateResult<TState>
    > = (prevResult) => {
      if (prevResult.status === `fallback`) {
        return prevResult.state === undefined
          ? {status: `fallback`, delayed: false}
          : {status: `completed`, state: prevResult.state};
      }

      return prevResult;
    };

    const handlePendingTimeout: React.SetStateAction<
      DelayedStateResult<TState>
    > = (prevResult) => {
      if (prevResult.status === `pending`) {
        delayedTimeoutHandle.current = window.setTimeout(() => {
          setResult(handleDelayedTimeout);
        }, delay);

        return {status: `fallback`, delayed: true};
      }

      return prevResult;
    };

    if (result.status === `pending`) {
      pendingTimeoutHandle.current = window.setTimeout(() => {
        setResult(handlePendingTimeout);
      }, threshold);
    }
  }, [result]);

  const setState = (state: TState) => {
    setResult((prevResult) => {
      if (prevResult.status === `fallback`) {
        return prevResult.delayed
          ? {status: `fallback`, delayed: true, state}
          : {status: `completed`, state};
      }

      window.clearTimeout(pendingTimeoutHandle.current);

      return {status: `completed`, state};
    });
  };

  const resetState = () => {
    setResult((prevResult) => {
      if (prevResult.status === `fallback` && prevResult.state !== undefined) {
        return {status: `fallback`, delayed: prevResult.delayed};
      }

      if (prevResult.status === `completed`) {
        window.clearTimeout(delayedTimeoutHandle.current);

        return {status: `pending`};
      }

      return prevResult;
    });
  };

  return [
    result,
    React.useCallback(
      (state) => (state === undefined ? resetState() : setState(state)),
      [],
    ),
  ];
}
